import React, { useEffect, useState } from 'react';
import { Calendar, Tag, MapPin, Search } from 'lucide-react';
import FilterSelect from './FilterSelect';
import type { FilterState, FilterOptions} from '@/types/event';

interface FilterSectionProps {
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
  filterOptions: FilterOptions;
}

const FilterSection: React.FC<FilterSectionProps> = ({ 
  filters, 
  onFilterChange, 
  filterOptions 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Debounce filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onFilterChange(localFilters);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [localFilters, onFilterChange]);

  const handleFilterUpdate = (newFilters: Partial<FilterState>) => {
    setLocalFilters(prev => ({ ...prev, ...newFilters }));
  };

  const getTagButtons = () => {
    if (!filterOptions.tag_buttons || Object.keys(filterOptions.tag_buttons).length === 0) {
      return [];
    }

    return Object.entries(filterOptions.tag_buttons).map(([key, tag]) => ({
      key,
      id: tag.id,
      slug: tag.slug,
      display: tag.display,
      count: tag.count
    }));
  };

  const tags = getTagButtons();

  return (
    <section className="mt-4 md:pt-12 pb-2 px-4 sm:px-6 md:px-8 lg:px-12 bg-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Tag Filters - Horizontal scroll on mobile */}
        {tags.length > 0 && (
          <div className="mb-4 md:mb-6 lg:mb-8">
            <div className="flex overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap gap-2 sm:gap-3 -mx-1 px-1 sm:mx-0 sm:px-0 scrollbar-thin scrollbar-thumb-orange-500/30 scrollbar-track-transparent [scrollbar-width:thin]">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleFilterUpdate({ 
                    tag: localFilters.tag === tag.id ? '' : tag.id as string
                  })}
                  className={`flex-shrink-0 px-3 sm:px-4 py-1.5 sm:py-2 backdrop-blur-sm border rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                    localFilters.tag === tag.id
                      ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                      : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                  }`}
                >
                  {tag.display}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-4 md:mb-6 lg:mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search events by title, description, or professional..."
              value={localFilters.search}
              onChange={(e) => handleFilterUpdate({ search: e.target.value })}
              className="w-full px-4 sm:px-5 py-2.5 sm:py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
            />
            <Search className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          </div>
        </div>

        {/* Filter Row */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <FilterSelect
              icon={Calendar}
              value={localFilters.date}
              onChange={(value) => handleFilterUpdate({ date: value })}
              options={[
                { value: '', label: 'All Dates' },
                ...Object.entries(filterOptions.date_filters || {}).map(([value, label]) => ({
                  value,
                  label: label as string
                }))
              ]}
            />
          </div>

          <div className="flex-1 min-w-0">
            <FilterSelect
              icon={MapPin}
              value={localFilters.location}
              onChange={(value) => handleFilterUpdate({ location: value })}
              options={[
                { value: '', label: 'All Locations' },
                ...Object.entries(filterOptions.locations || {}).map(([value, label]) => ({
                  value,
                  label: label as string
                }))
              ]}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilterSection;